import { GenericObject } from "@mongez/reinforcements";
import { useLocation, useSearchParams } from "@remix-run/react";
import { filtersParamsAtom } from "core/catalog/atoms";
import { useCallback, useEffect, useState } from "react";

export function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const location = useLocation();

  const [activeFilters, setActiveFilters] = useState<
    Record<string, string | string[] | null>
  >({});

  const filtersParams = filtersParamsAtom.useValue();

  useEffect(() => {
    const filters: Record<string, string | string[] | null> = {};

    searchParams.forEach((value, key) => {
      if (key.endsWith("[]")) {
        const baseKey = key.replace("[]", "");
        filters[baseKey] = searchParams.getAll(key);
      } else {
        filters[key] = value;
      }
    });

    setActiveFilters(filters);
  }, [location.search, searchParams]);

  const setFilters = useCallback(
    (
      newFilters:
        | Record<string, string | string[] | null>
        | ((prevFilters: GenericObject) => GenericObject),
    ) => {
      // Create a new URLSearchParams from current searchParams
      const params = new URLSearchParams(searchParams.toString());

      const filters =
        typeof newFilters === "function"
          ? newFilters(activeFilters)
          : newFilters;

      for (const [key, value] of Object.entries(filters)) {
        if (value === null || value === undefined) {
          params.delete(key);
          params.delete(`${key}[]`);
        } else if (Array.isArray(value)) {
          params.delete(key);
          params.delete(`${key}[]`);
          value.forEach((v) => params.append(`${key}[]`, v));
        } else {
          params.set(key, value);
        }
      }

      filtersParamsAtom.update(params);
    },
    [activeFilters, searchParams],
  );

  const applyFilters = useCallback(() => {
    setSearchParams(filtersParams, { preventScrollReset: true });
  }, [filtersParams, setSearchParams]);

  const resetFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { preventScrollReset: true });
  }, [setSearchParams]);

  return { activeFilters, setFilters, resetFilters, applyFilters };
}
